import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToolbar } from './useToolbar';
import dayjs from 'dayjs';
import { Icon } from '@mui/material';
import { CoreContext, RbToolbarActionButton, type RbContextMenuPosition, type RbRowSelectionModel } from '@scmlatam/web-ui';
import type { Column, Options, Row } from '../types/hooks';
import type { Employee } from '../types/services';
import { useAuthorization } from '../../../../contexts/AuthorizationContext';
import { usePermisoEmpleado as usePermiso } from '../../../../auth/permisosModulos';

const useTable = () => {
    const { t } = useTranslation('workers.table');
    const { format } = useContext(CoreContext);
    const { canView } = useAuthorization();

    const canUpdateEmployee = usePermiso('editar-empleado');
    const canAssignContract = usePermiso('asignar-contrato');
    const canAssignPosition = usePermiso('asignar-puesto');
    const canAssignPayRule = usePermiso('asignar-regla-pago');
    const canAssignScheduleRule = usePermiso('asignar-regla-horario');
    const canAssignAccrualProfile = usePermiso('regla-acumulacion');
    const canAssignTag = usePermiso('asignar-etiqueta');

    const canOpenEmployeeActions = canView('/timekeeper?view=timecard') || canView('/timekeeper?view=schedule') || canView('/reports?view=results_report');

    const toolbarProps = useToolbar();

    const columns: Column[] = [
        {
            key: 'fullname',
            header: t('columns.fullname'),
            width: '15vw',
        },
        {
            key: 'person_number',
            header: t('columns.person_number'),
            width: '15vw',
        },
        {
            key: 'contract_start_date',
            header: t('columns.contract_start_date'),
            width: '10vw',
        },
        {
            key: 'contract_end_date',
            header: t('columns.contract_end_date'),
            width: '10vw',
        },
        {
            key: 'orgpath',
            header: t('columns.orgpath'),
            width: '25vw',
        },
        {
            key: 'closed_period',
            header: t('columns.closed_payment_period'),
            width: '15vw',
        },
        {
            key: 'status',
            header: t('columns.status'),
            width: '5vw',
            render: (row: Row) => {
                const isActive = row.status === 'Active';
                return (
                    <div className="flex items-center gap-2">
                        <Icon
                            baseClassName="material-symbols-rounded"
                            sx={{
                                color: isActive ? '#58CC00' : '#9D9A95',
                                fontSize: '12px',
                                mb: 0.5,
                            }}
                        >
                            circle
                        </Icon>

                        <span>{isActive ? t('table.active') : t('table.inactive')}</span>
                    </div>
                );
            },
        },
        {
            key: 'actions',
            header: t('columns.actions'),
            width: '2vw',
            render: (row: Row) => {
                return (
                    <RbToolbarActionButton
                        variant="icon"
                        icon={
                            <Icon
                                baseClassName="material-symbols-rounded"
                                sx={{ color: '#190007', fontSize: '18px' }}
                                onClick={(event) => {
                                    setRowId(row.id);
                                    setOption(null);

                                    const rect = event.currentTarget.getBoundingClientRect();

                                    setContextMenu({
                                        x: rect.left,
                                        y: rect.bottom + 6,
                                    });

                                    setVariant('light');
                                }}
                            >
                                more_horiz
                            </Icon>
                        }
                    />
                );
            },
        },
    ];

    const [variant, setVariant] = useState<'light' | 'dark'>('light');

    const [rows, setRows] = useState<Row[]>([]);
    const [rowsFiltered, setRowsFiltered] = useState<Row[]>([]);

    const [rowSelected, setRowSelected] = useState<RbRowSelectionModel>({
        ids: new Set(),
        type: 'include',
    });

    const [rowId, setRowId] = useState<string | null>(null);

    const [option, setOption] = useState<Options | null>(null);

    const [contextMenu, setContextMenu] = useState<RbContextMenuPosition | null>(null);

    const selectedIds = !Array.isArray(rowSelected) && rowSelected && typeof rowSelected === 'object' && 'ids' in rowSelected ? rowSelected.ids : rowSelected;

    const selectedIdsSet = new Set(selectedIds instanceof Set ? Array.from(selectedIds) : Array.isArray(selectedIds) ? selectedIds : []);

    const selectedRowIds = selectedIdsSet.size > 0 ? selectedIdsSet : rowId ? new Set([rowId]) : new Set<string>();

    const navigateToSchedule = () => {
        const searchParams = new URLSearchParams(window.location.search);

        const startDate = searchParams.get('start_date') || dayjs().startOf('month').format('YYYY-MM-DD');

        const endDate = searchParams.get('end_date') || dayjs().endOf('month').format('YYYY-MM-DD');

        const tagsFromUrl = searchParams.get('tags') || '';

        const employees = Array.from(selectedRowIds)
            .map((id) => id?.toString())
            .filter((id): id is string => Boolean(id))
            .join(',');

        console.log('employees', employees);

        const params = new URLSearchParams({
            module: 'timekeeper',
            view: 'schedule',
            start_date: startDate,
            end_date: endDate,
            tags: tagsFromUrl,
            employees,
        });

        window.history.pushState(null, '', `${window.location.origin}/timekeeper?${params.toString()}`);

        window.dispatchEvent(new PopStateEvent('popstate'));
    };

    const navigateToExecuteReport = () => {
        const searchParams = new URLSearchParams(window.location.search);

        const startDate = searchParams.get('start_date') || dayjs().startOf('month').format('YYYY-MM-DD');

        const endDate = searchParams.get('end_date') || dayjs().endOf('month').format('YYYY-MM-DD');

        const tagsFromUrl = searchParams.get('tags') || '';

        const employees = Array.from(selectedRowIds)
            .map((id) => id?.toString())
            .filter((id): id is string => Boolean(id))
            .join(',');

        window.history.pushState(null, '', `/reports?module=reports&view=execute_report&start_date=${startDate}&end_date=${endDate}&tags=${tagsFromUrl}&employees=${employees}`);

        window.dispatchEvent(new PopStateEvent('popstate'));
    };

    const items = [
        {
            id: 'menu-editar-empleado',
            label: t('menu.edit_employee'),
            icon: <Icon baseClassName="material-symbols-rounded">edit_square</Icon>,
            onClick: () => setOption('update_employee'),
            group: 'light',
        },
        {
            id: 'menu-desactivar-empleado',
            label: t('menu.deactivate_employee'),
            icon: <Icon baseClassName="material-symbols-rounded">person_off</Icon>,
            danger: true,
            onClick: () => setOption('deactivate_employee'),
            group: 'light',
        },
        {
            id: 'menu-asignar-contrato',
            label: t('menu.assign_contract'),
            icon: <Icon baseClassName="material-symbols-rounded">contract</Icon>,
            onClick: () => setOption('assign_contract'),
            group: 'light',
        },
        {
            id: 'menu-asignar-puesto',
            label: t('menu.assign_position'),
            icon: <Icon baseClassName="material-symbols-rounded">graph_2</Icon>,
            onClick: () => setOption('assign_position'),
            group: 'light',
        },
        {
            id: 'menu-ir-timecard',
            label: t('menu.go_to_timecard'),
            icon: <Icon baseClassName="material-symbols-rounded">acute</Icon>,
            onClick: () => {
                toolbarProps.reload();
            },
            group: 'dark',
        },
        {
            id: 'menu-ir-horario',
            label: t('menu.go_to_schedule'),
            icon: <Icon baseClassName="material-symbols-rounded">acute</Icon>,
            onClick: navigateToSchedule,
            group: 'dark',
        },
        {
            id: 'menu-ir-reportes',
            label: t('menu.go_to_reports'),
            icon: <Icon baseClassName="material-symbols-rounded">article</Icon>,
            onClick: navigateToExecuteReport,
            group: 'dark',
        },
        {
            id: 'menu-asignar-etiqueta',
            label: t('menu.assign_tag'),
            icon: <Icon baseClassName="material-symbols-rounded">sell</Icon>,
            disabled: selectedRowIds.size === 0,
            onClick: () => setOption('assign_tag'),
            group: 'light',
        },
        {
            id: 'menu-regla-acumulacion',
            label: t('menu.assign_accrual_profile'),
            icon: <Icon baseClassName="material-symbols-rounded">manage_accounts</Icon>,
            disabled: selectedRowIds.size !== 1,
            onClick: () => setOption('assign_accrual_profile'),
            group: 'light',
        },
        {
            id: 'menu-asignar-regla-horario',
            label: t('menu.assign_schedule_rule'),
            icon: <Icon baseClassName="material-symbols-rounded">calendar_month</Icon>,
            disabled: selectedRowIds.size !== 1,
            onClick: () => setOption('assign_schedule_rule'),
            group: 'light',
        },
        {
            id: 'menu-asignar-regla-pago',
            label: t('menu.assign_pay_rule'),
            icon: <Icon baseClassName="material-symbols-rounded">payments</Icon>,
            disabled: selectedRowIds.size !== 1,
            onClick: () => setOption('assign_pay_rule'),
            group: 'light',
        },
        {
            id: 'menu-recargar',
            label: t('menu.reload'),
            icon: <Icon baseClassName="material-symbols-rounded">refresh</Icon>,
            dividerBefore: true,
            onClick: () => {
                toolbarProps.reload();
            },
            group: 'both',
        },
    ];

    const permisosMenu: Record<string, boolean> = {
        'menu-editar-empleado': canUpdateEmployee,
        'menu-asignar-contrato': canAssignContract,
        'menu-asignar-puesto': canAssignPosition,
        'menu-asignar-regla-pago': canAssignPayRule,
        'menu-asignar-regla-horario': canAssignScheduleRule,
        'menu-regla-acumulacion': canAssignAccrualProfile,
        'menu-asignar-etiqueta': canAssignTag,
    };

    const visibleItems = items.filter((item) => permisosMenu[item.id] ?? true);

    const createRows = (employees: Employee[]) => {
        const temporalRows: Row[] = [];
        const employeeIds = new Set<string>();

        for (const employee of employees) {
            if (employeeIds.has(employee.employee_id)) {
                continue;
            }

            employeeIds.add(employee.employee_id);

            const orgpaths = employee.orgpath.sort((a, b) => dayjs(a.Range.end_date).diff(dayjs(b.Range.end_date)));

            const orgpathInRangeOfSearch = orgpaths.filter((o) => toolbarProps.period[0]?.isBefore(dayjs(o?.Range?.end_date)) && dayjs(o?.Range?.start_date).isBefore(toolbarProps.period[1]));

            let orgpath = '';
            let contract_start_date = '';
            let contract_end_date = '';

            if (orgpathInRangeOfSearch.length > 0) {
                let lastOrgpath = orgpathInRangeOfSearch[orgpathInRangeOfSearch.length - 1];

                orgpath = `${lastOrgpath.Area.map((area) => area.name).join('/')}/${lastOrgpath.Posicion.name}`;

                contract_start_date = `${dayjs(lastOrgpath.Range.start_date).format(format)}`;

                contract_end_date = `${dayjs(lastOrgpath.Range.end_date).format(format)}`;
            }

            const row: Row = {
                id: employee.employee_id,
                created_at: employee.created_at,
                fullname: `${employee.first_name} ${employee.last_name}`,
                person_number: employee.document_employee,
                contract_start_date,
                contract_end_date,
                orgpath,
                orgpaths: employee.orgpath as unknown as Row['orgpaths'],
                closed_period: dayjs(employee.pay_period_end).format(format),
                status: employee.active ? 'Active' : 'Inactive',
                actions: [],
            };

            temporalRows.push(row);
        }

        setRows(temporalRows);
        setRowsFiltered(temporalRows);

        console.log('temporalRows', temporalRows);
    };

    const searchParams = new URLSearchParams(window.location.search);

    const startDate = searchParams.get('start_date');
    const endDate = searchParams.get('end_date');
    const tagIds = searchParams.get('tags');
    const filterValue = toolbarProps.filter.trim().toLocaleLowerCase();

    const filteredRows = rows.filter((row) =>
        [row.fullname, row.person_number, row.contract_start_date, row.contract_end_date, row.orgpath, row.closed_period, row.status].some((value) => value.toLocaleLowerCase().includes(filterValue)),
    );

    useEffect(() => {
        createRows(toolbarProps.employees);
    }, [toolbarProps.employees]);

    useEffect(() => {
        setRowsFiltered(filteredRows);
    }, [toolbarProps.filter]);

    return {
        ...toolbarProps,
        columns,
        rows: rowsFiltered,
        rowSelected,
        setRowSelected,
        rowId,
        setRowId,
        option,
        setOption,
        contextMenu,
        setContextMenu,
        items: visibleItems,
        selectedRowIds,
        variant,
        setVariant,
        startDate,
        endDate,
        tagIds,
        canOpenEmployeeActions,
    };
};

export { useTable };
